import React, { useEffect, useMemo, useState } from "react";

export default function PatientVisitFilterCard({
  isOpen,
  onClose,
  onApplyFilter,
  currentFilters = {},
  SETTINGS = {},
  KEY_NOTES = {},
}) {
  const [preset, setPreset] = useState("this_year_to_date");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [setting, setSetting] = useState("");
  const [keyNote, setKeyNote] = useState("");

  const yyyyMmDd = (d) =>
    new Date(d.getTime() - d.getTimezoneOffset() * 60000)
      .toISOString()
      .slice(0, 10);

  const computePresetRange = (value) => {
    const now = new Date();
    if (value === "this_year_to_date") {
      const start = new Date(now.getFullYear(), 0, 1);
      return { from: yyyyMmDd(start), to: yyyyMmDd(now) };
    }
    if (value === "last_year") {
      const start = new Date(now.getFullYear() - 1, 0, 1);
      const end = new Date(now.getFullYear() - 1, 11, 31);
      return { from: yyyyMmDd(start), to: yyyyMmDd(end) };
    }
    if (value === "last_month") {
      const firstDayThisMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endLastMonth = new Date(firstDayThisMonth - 1);
      const startLastMonth = new Date(
        endLastMonth.getFullYear(),
        endLastMonth.getMonth(),
        1,
      );
      return { from: yyyyMmDd(startLastMonth), to: yyyyMmDd(endLastMonth) };
    }
    // custom
    return { from: dateFrom || "", to: dateTo || "" };
  };

  useEffect(() => {
    if (preset !== "custom") {
      const { from, to } = computePresetRange(preset);
      setDateFrom(from);
      setDateTo(to);
    }
  }, [preset]);

  useEffect(() => {
    // seed from current filters if provided
    if (currentFilters) {
      if (currentFilters.dateFrom) setDateFrom(currentFilters.dateFrom);
      if (currentFilters.dateTo) setDateTo(currentFilters.dateTo);
      if (
        currentFilters.setting !== undefined &&
        currentFilters.setting !== null
      ) {
        setSetting(String(currentFilters.setting));
      }
      if (
        currentFilters.keyNote !== undefined &&
        currentFilters.keyNote !== null
      ) {
        setKeyNote(String(currentFilters.keyNote));
      }
    }
  }, [currentFilters]);

  const handleRunReport = () => {
    const payload = {
      dateFrom: dateFrom || undefined,
      dateTo: dateTo || undefined,
      setting: setting ? Number(setting) : undefined,
      keyNote: keyNote ? Number(keyNote) : undefined,
    };
    onApplyFilter(payload);
  };

  const handleClearFilters = () => {
    setPreset("custom");
    setDateFrom("");
    setDateTo("");
    setSetting("");
    setKeyNote("");
    onApplyFilter({});
  };

  const isCustom = preset === "custom";

  if (!isOpen) return null;

  return (
    <div className="filter-card">
      <div className="filter-card-header">
        <div className="filter-card-title">Filters</div>
        <button
          className="filter-card-close"
          onClick={onClose}
          aria-label="Close filters"
          type="button"
        >
          <svg
            width="14"
            height="14"
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <path
              d="M18 6L6 18M6 6l12 12"
              stroke="#111827"
              strokeWidth="2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </div>

      <div className="filter-row">
        <div className="filter-group">
          <div className="filter-label">Date of visit</div>
          <div style={{ display: "flex", gap: 12 }}>
            <select
              className="filter-input"
              value={preset}
              onChange={(e) => setPreset(e.target.value)}
            >
              <option value="this_year_to_date">This Year-to-date</option>
              <option value="last_year">Last Year</option>
              <option value="last_month">Last Month</option>
              <option value="custom">Custom</option>
            </select>
            <input
              type="date"
              className="filter-input"
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              disabled={!isCustom}
            />
            <input
              type="date"
              className="filter-input"
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              disabled={!isCustom}
            />
          </div>
        </div>
      </div>

      <div className="filter-row">
        <div className="filter-group">
          <div className="filter-label">Setting</div>
          <select
            className="filter-input"
            value={setting}
            onChange={(e) => setSetting(e.target.value)}
          >
            <option value="">All</option>
            {Object.entries(SETTINGS).map(([value, label]) => (
              <option value={value} key={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-group">
          <div className="filter-label">Key Notes</div>
          <select
            className="filter-input"
            value={keyNote}
            onChange={(e) => setKeyNote(e.target.value)}
          >
            <option value="">All</option>
            {Object.entries(KEY_NOTES).map(([value, label]) => (
              <option value={value} key={value}>
                {label}
              </option>
            ))}
          </select>
        </div>
        <div className="filter-actions">
          <button
            className="clear-filter-btn"
            type="button"
            onClick={handleClearFilters}
          >
            Clear filters
          </button>
          <button
            className="run-report-btn"
            type="button"
            onClick={handleRunReport}
          >
            Run report
          </button>
        </div>
      </div>
    </div>
  );
}
